import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/components/ui/use-toast';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, Save, FileText, AlertCircle, Plus, Trash2,
  ChevronDown, ChevronRight, Lock, Unlock,
  CheckCircle2, Sparkles,
  Database, Shield, Zap, Filter, RefreshCw, Edit2, X, GripVertical,
  Type, Mail, Phone, Globe, Calendar, Hash, MapPin
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '@/components/AuthProvider';
import { db } from '@/lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { cn } from '@/lib/utils';
import {
  ApplicationSource,
  ApplicationSourceSettings,
  FieldSetting,
  DocumentRequirement,
  SectionConfig,
  DEFAULT_FIELD_DEFINITIONS,
  DEFAULT_DOCUMENT_REQUIREMENTS,
  APPLICATION_SECTIONS,
  DEFAULT_SECTION_ORDER,
  DEFAULT_SECTION_CONFIGS,
  IndustryClassification,
  ClassificationSettings,
  BankingPartnerConfiguration,
  createClassificationSettings,
} from '@/features/merchant-application/types/fieldSettings';
import { DEFAULT_BANKING_PARTNER_CONFIG } from '@/config/default-banking-partner-config';
import type { PipelineStatus } from '@/features/merchant-application/types';
import type { MerchantApplicationSettings as MerchantApplicationSettingsType } from '@/features/merchant-application/types/fieldSettings';
import { bankingPartnerService } from '@/features/banking-partners/services/bankingPartnerService';
import type { BankingPartner } from '@/features/banking-partners/types';
import { applicationSourceService } from '@/features/application-sources/services/applicationSourceService';
import type { ApplicationSource as ApplicationSourceType } from '@/features/application-sources/types';

// Helper function to create default source settings
const createDefaultSourceSettings = (
  source: ApplicationSource,
  displayName: string,
  isCustom: boolean = false
): ApplicationSourceSettings => {
  const fieldSettings = Object.keys(DEFAULT_FIELD_DEFINITIONS)
    .filter(fieldPath => !fieldPath.includes('personalAddress')) // Exclude personal address fields
    .reduce((acc, fieldPath) => {
      const definition = DEFAULT_FIELD_DEFINITIONS[fieldPath];
      acc[fieldPath] = {
        fieldName: fieldPath.split('.').pop() || '',
        fieldPath,
        label: definition.label || '',
        section: definition.section || 'Other',
        placeholder: definition.placeholder || '',
        helpText: definition.helpText || '',
        order: definition.order || 999,
        enabled: true,
        required: true,
        validation: definition.validation || undefined,
        defaultValue: definition.defaultValue || undefined,
      } as FieldSetting;
      return acc;
    }, {} as Record<string, FieldSetting>);

  return {
    source,
    displayName,
    fieldSettings,
    documentRequirements: [...DEFAULT_DOCUMENT_REQUIREMENTS],
    sectionConfigs: { ...DEFAULT_SECTION_CONFIGS },
    sectionVisibility: {
      [APPLICATION_SECTIONS.PERSONAL_INFO]: true,
      [APPLICATION_SECTIONS.CORPORATION_INFO]: true,
      [APPLICATION_SECTIONS.PROCESSING_HISTORY]: true,
      [APPLICATION_SECTIONS.BENEFICIAL_OWNERS]: true,
      [APPLICATION_SECTIONS.BANK_DETAILS]: true,
      [APPLICATION_SECTIONS.DOCUMENTATION]: true,
    },
    sectionOrder: [...DEFAULT_SECTION_ORDER],
    defaultPipelineStatus: 'lead' as PipelineStatus,
    isCustom,
  };
};

// Helper function to get current classification settings
const getCurrentClassificationSettings = (
  sourceSettings: ApplicationSourceSettings,
  bankingPartnerId?: string,
  classification?: IndustryClassification
): ClassificationSettings | null => {
  // If banking partner configurations exist, use the new nested structure
  if (sourceSettings.bankingPartnerConfigurations && bankingPartnerId && classification) {
    const config = sourceSettings.bankingPartnerConfigurations[bankingPartnerId]?.[classification];
    if (config) return config;
  }

  // Fall back to legacy structure
  if (sourceSettings.fieldSettings && sourceSettings.documentRequirements) {
    return {
      fieldSettings: sourceSettings.fieldSettings,
      documentRequirements: sourceSettings.documentRequirements,
      sectionVisibility: sourceSettings.sectionVisibility,
      sectionOrder: sourceSettings.sectionOrder,
      sectionConfigs: sourceSettings.sectionConfigs,
    };
  }

  // Fall back to static default configuration if no banking partner configurations exist
  if (classification && DEFAULT_BANKING_PARTNER_CONFIG[classification]) {
    console.log(`📥 Using static default config for classification: ${classification}`);

    const config = { ...DEFAULT_BANKING_PARTNER_CONFIG[classification] };

    // Remove Processing History section assignment from low-risk fields
    if (classification === 'low-risk' && config.fieldSettings) {
      const fieldSettings = { ...config.fieldSettings };
      const processingHistoryFields = Object.keys(fieldSettings).filter(
        fieldPath => fieldPath.startsWith('processingHistory.')
      );

      processingHistoryFields.forEach(fieldPath => {
        if (fieldSettings[fieldPath]) {
          // Remove section property to prevent the section from appearing
          const { section, ...rest } = fieldSettings[fieldPath];
          fieldSettings[fieldPath] = rest as FieldSetting;
        }
      });

      config.fieldSettings = fieldSettings;
      console.log(`🚫 Removed Processing History section from ${processingHistoryFields.length} fields for low-risk classification`);
    }

    return config;
  }

  return null;
};

// Helper function to update nested classification settings
const updateNestedClassificationSettings = (
  sourceSettings: ApplicationSourceSettings,
  bankingPartnerId: string | undefined,
  classification: IndustryClassification | undefined,
  updates: Partial<ClassificationSettings>
): ApplicationSourceSettings => {
  // If we have banking partner and classification, update nested structure
  if (bankingPartnerId && classification) {
    const existingConfigs = sourceSettings.bankingPartnerConfigurations || {};
    const existingBankingPartner = existingConfigs[bankingPartnerId] || {} as Partial<Record<IndustryClassification, ClassificationSettings>>;

    // If this classification doesn't exist yet, initialize it from legacy data or defaults
    let existingClassification = existingBankingPartner[classification];
    if (!existingClassification) {
      // Try to migrate from legacy structure if it exists
      if (sourceSettings.fieldSettings && sourceSettings.documentRequirements) {
        existingClassification = {
          fieldSettings: sourceSettings.fieldSettings,
          documentRequirements: sourceSettings.documentRequirements,
          sectionVisibility: sourceSettings.sectionVisibility,
          sectionOrder: sourceSettings.sectionOrder,
          sectionConfigs: sourceSettings.sectionConfigs,
        };
      } else {
        // Otherwise use defaults
        existingClassification = createClassificationSettings();
      }
    }

    return {
      ...sourceSettings,
      bankingPartnerConfigurations: {
        ...existingConfigs,
        [bankingPartnerId]: {
          ...existingBankingPartner,
          [classification]: {
            ...existingClassification,
            ...updates,
          },
        } as Record<IndustryClassification, ClassificationSettings>,
      },
    };
  }

  // Fall back to updating legacy structure
  return {
    ...sourceSettings,
    ...updates,
  };
};

interface MerchantApplicationSettingsProps {
  onSettingsChange?: (hasChanges: boolean) => void;
  onSave?: () => void;
}

export interface MerchantApplicationSettingsRef {
  save: () => Promise<void>;
}

// Sortable Field Component
interface SortableFieldProps {
  fieldPath: string;
  field: FieldSetting;
  onUpdateFieldSetting: (fieldPath: string, updates: Partial<FieldSetting>) => void;
}

function SortableField({ fieldPath, field, onUpdateFieldSetting }: SortableFieldProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: fieldPath });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const loweredFieldPath = fieldPath.toLowerCase();
  const supportsLinkToggle =
    loweredFieldPath.includes('website') ||
    loweredFieldPath.includes('email') ||
    loweredFieldPath.includes('phone');
  const selectFieldOptions: Record<string, Array<{ label: string; value: string }>> = {
    'bankDetails.accountType': [
      { label: 'Checking', value: 'checking' },
      { label: 'Savings', value: 'savings' },
    ],
    'processingHistory.whenIsCustomerCharged': [
      { label: 'At order', value: 'order' },
      { label: 'At delivery', value: 'delivery' },
      { label: 'At shipment', value: 'shipment' },
    ],
    'processingHistory.advancedDeposits': [
      { label: 'No', value: 'no' },
      { label: 'Yes', value: 'yes' },
    ],
  };
  const selectOptions = selectFieldOptions[fieldPath];
  const isSelectField = Array.isArray(selectOptions);
  const selectFieldHelpText: Record<string, string> = {
    'bankDetails.accountType': 'Used by API when hidden (01 for checking, 02 for savings)',
    'processingHistory.whenIsCustomerCharged': 'Used by API when hidden (order/delivery/shipment tokens)',
    'processingHistory.advancedDeposits': 'Used by API when hidden (yes/no)',
  };

  const inferInputType = () => {
    if (loweredFieldPath.includes('email')) return 'email';
    if (loweredFieldPath.includes('phone')) return 'tel';
    if (loweredFieldPath.includes('date')) return 'date';
    if (field.validation?.min !== undefined || field.validation?.max !== undefined) return 'number';
    return 'text';
  };
  const inputType = inferInputType();

  const validateDefault = (val: unknown): string | null => {
    const isEmpty =
      val === null ||
      val === undefined ||
      (typeof val === 'string' && val.trim() === '');
    if (isEmpty) {
      return null; // empty is allowed unless required (display hint separately)
    }
    if (isSelectField) {
      const normalized = String(val).toLowerCase();
      const matched = selectOptions?.some((opt) => opt.value === normalized);
      return matched ? null : 'Select a valid option';
    }
    const str = String(val);
    const v = field.validation;
    if (inputType === 'number') {
      const num = Number(val);
      if (Number.isNaN(num)) return 'Default must be a number';
      if (v?.min !== undefined && num < v.min) return `Must be ≥ ${v.min}`;
      if (v?.max !== undefined && num > v.max) return `Must be ≤ ${v.max}`;
    }
    if (v?.minLength !== undefined && str.length < v.minLength) return `Min length ${v.minLength}`;
    if (v?.maxLength !== undefined && str.length > v.maxLength) return `Max length ${v.maxLength}`;
    if (v?.pattern) {
      try {
        const re = new RegExp(v.pattern);
        if (!re.test(str)) return 'Does not match expected format';
      } catch {
        // Ignore invalid patterns defined in settings
      }
    }
    return null;
  };

  const defaultError = !field.enabled && field.required && !field.defaultValue
    ? 'Default required when hidden'
    : validateDefault(field.defaultValue);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'relative rounded-xl border bg-white transition-all duration-200',
        field.enabled
          ? 'border-slate-200 shadow-sm hover:border-indigo-200 hover:shadow-md'
          : 'border-slate-200 bg-slate-50',
        isDragging && 'shadow-lg ring-2 ring-indigo-200'
      )}
    >
      <div className="flex flex-col gap-4 p-4 md:gap-5">
        <div className="flex flex-wrap items-start gap-4">
          <div
            {...attributes}
            {...listeners}
            className="mt-1 cursor-grab rounded-md p-1 text-slate-300 transition-colors hover:text-slate-500 active:cursor-grabbing"
          >
            <GripVertical className="h-3.5 w-3.5" />
          </div>
          <Checkbox
            checked={field.enabled}
            onCheckedChange={(checked) =>
              onUpdateFieldSetting(fieldPath, { enabled: checked as boolean })
            }
            className="mt-1"
          />
          <div className="flex min-w-0 flex-1 flex-col gap-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex min-w-0 flex-1 items-start gap-3">
                <div
                  className={cn(
                    'mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg border text-sm',
                    field.enabled
                      ? 'border-indigo-100 bg-indigo-50 text-indigo-500'
                      : 'border-slate-200 bg-white text-slate-400'
                  )}
                >
                  {(() => {
                    if (loweredFieldPath.includes('email')) return <Mail className="h-4 w-4" />;
                    if (loweredFieldPath.includes('phone')) return <Phone className="h-4 w-4" />;
                    if (loweredFieldPath.includes('website')) return <Globe className="h-4 w-4" />;
                    if (loweredFieldPath.includes('date')) return <Calendar className="h-4 w-4" />;
                    if (
                      loweredFieldPath.includes('zip') ||
                      loweredFieldPath.includes('ein') ||
                      loweredFieldPath.includes('sic')
                    )
                      return <Hash className="h-4 w-4" />;
                    if (
                      loweredFieldPath.includes('address') ||
                      loweredFieldPath.endsWith('.city') ||
                      loweredFieldPath.endsWith('.state')
                    )
                      return <MapPin className="h-4 w-4" />;
                    if (loweredFieldPath.includes('description'))
                      return <FileText className="h-4 w-4" />;
                    return <Type className="h-4 w-4" />;
                  })()}
                </div>
                <div className="min-w-0 flex-1">
                  <Label
                    className={cn(
                      'block text-base font-semibold leading-tight text-slate-900',
                      !field.enabled && 'text-slate-600'
                    )}
                  >
                    {field.label}
                  </Label>
                  {field.helpText && (
                    <p className="mt-1 text-sm leading-relaxed text-slate-500">{field.helpText}</p>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs font-medium">
                <Badge
                  variant="secondary"
                  className={cn(
                    'rounded-full border-0 px-3 py-1 text-[11px]',
                    field.enabled
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-slate-200 text-slate-600'
                  )}
                >
                  {field.enabled ? 'Shown' : 'Hidden'}
                </Badge>
                <Badge
                  variant="secondary"
                  className={cn(
                    'rounded-full border-0 px-3 py-1 text-[11px]',
                    field.required
                      ? 'bg-rose-100 text-rose-700'
                      : 'bg-slate-200 text-slate-600'
                  )}
                >
                  {field.required ? 'Required' : 'Optional'}
                </Badge>
                {supportsLinkToggle && (
                  <Badge
                    variant="secondary"
                    className={cn(
                      'rounded-full border-0 px-3 py-1 text-[11px]',
                      field.linkEnabled !== false
                        ? 'bg-sky-100 text-sky-700'
                        : 'bg-slate-200 text-slate-600'
                    )}
                  >
                    {field.linkEnabled !== false ? 'Link on' : 'Link off'}
                  </Badge>
                )}
              </div>
            </div>

            {field.enabled ? (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2 text-xs sm:text-sm">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="secondary"
                      className={cn(
                        'border-0 px-2.5 py-1 text-xs font-medium',
                        field.required
                          ? 'bg-rose-100 text-rose-700'
                          : 'bg-slate-200 text-slate-600'
                      )}
                    >
                      {field.required ? (
                        <span className="flex items-center gap-1">
                          <Lock className="h-3 w-3" />
                          Required
                        </span>
                      ) : (
                        <span className="flex items-center gap-1">
                          <Unlock className="h-3 w-3" />
                          Optional
                        </span>
                      )}
                    </Badge>
                    <Switch
                      checked={field.required}
                      onCheckedChange={(checked) =>
                        onUpdateFieldSetting(fieldPath, { required: checked })
                      }
                      className="scale-90 data-[state=checked]:bg-green-500"
                    />
                  </div>
                  {supportsLinkToggle && (
                    <div className="flex items-center gap-2 text-xs font-medium text-slate-600">
                      <Label className="text-xs uppercase tracking-wide text-slate-500">Link</Label>
                      <Switch
                        checked={field.linkEnabled !== false}
                        onCheckedChange={(checked) =>
                          onUpdateFieldSetting(fieldPath, { linkEnabled: !!checked })
                        }
                        className="scale-90 data-[state=checked]:bg-sky-500"
                      />
                    </div>
                  )}
                </div>
                {isSelectField && selectOptions && (
                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-indigo-100 bg-indigo-50/60 px-3 py-2 text-xs sm:text-sm">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">Default Selection</span>
                    <Select
                      value={
                        typeof field.defaultValue === 'string' && selectOptions.some((option) => option.value === field.defaultValue)
                          ? (field.defaultValue as string)
                          : selectOptions[0]?.value || ''
                      }
                      onValueChange={(value) => onUpdateFieldSetting(fieldPath, { defaultValue: value })}
                    >
                      <SelectTrigger className="h-8 w-40 border-indigo-200 focus-visible:ring-indigo-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {selectOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            ) : (
              <div className="rounded-lg border border-dashed border-slate-300 bg-white/80 px-3 py-2 text-xs sm:text-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Badge variant="secondary" className="border-0 bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-600">
                      Hidden Field
                    </Badge>
                    <span className="text-xs font-medium">Default</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    {isSelectField && selectOptions ? (
                      <Select
                        value={
                          typeof field.defaultValue === 'string' && selectOptions.some((option) => option.value === field.defaultValue)
                            ? (field.defaultValue as string)
                            : undefined
                        }
                        onValueChange={(value) => onUpdateFieldSetting(fieldPath, { defaultValue: value })}
                      >
                        <SelectTrigger className={cn(
                          'h-8 w-40 text-xs border-slate-300',
                          defaultError ? 'border-rose-300 focus-visible:ring-rose-200' : ''
                        )}>
                          <SelectValue placeholder="Select option" />
                        </SelectTrigger>
                        <SelectContent>
                          {selectOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        type={inputType}
                        value={field.defaultValue ?? ''}
                        onChange={(e) =>
                          onUpdateFieldSetting(fieldPath, { defaultValue: e.target.value })
                        }
                        placeholder="None"
                        className={cn(
                          'h-8 w-40 text-xs',
                          defaultError ? 'border-rose-300 focus-visible:ring-rose-200' : ''
                        )}
                      />
                    )}
                    {!defaultError && (
                      <span className="text-[11px] text-slate-500">
                        {selectFieldHelpText[fieldPath] || 'Used by API when hidden'}
                      </span>
                    )}
                  </div>
                </div>
                {defaultError && (
                  <span className="mt-2 block text-xs font-medium text-rose-600">{defaultError}</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Sortable Section Component
interface SortableSectionProps {
  section: string;
  fields: Array<{ fieldPath: string; field: FieldSetting }>;
  isExpanded: boolean;
  enabledCount: number;
  isSectionVisible: boolean;
  sectionConfig: SectionConfig;
  onToggleSection: (section: string) => void;
  onUpdateSectionVisibility: (section: string, visible: boolean) => void;
  onUpdateFieldSetting: (fieldPath: string, updates: Partial<FieldSetting>) => void;
  onHandleFieldDragEnd: (section: string, event: DragEndEvent) => void;
  onUpdateSectionConfig: (section: string, updates: Partial<SectionConfig>) => void;
}

function SortableSection({
  section,
  fields,
  isExpanded,
  enabledCount,
  isSectionVisible,
  sectionConfig,
  onToggleSection,
  onUpdateSectionVisibility,
  onUpdateFieldSetting,
  onHandleFieldDragEnd,
  onUpdateSectionConfig,
}: SortableSectionProps) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingSubtitle, setEditingSubtitle] = useState(false);
  const [tempTitle, setTempTitle] = useState(sectionConfig.title);
  const [tempSubtitle, setTempSubtitle] = useState(sectionConfig.subtitle);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section });

  // Move useSensor to top level to avoid conditional hook calls
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 3,
    },
  });

  const handleTitleSave = () => {
    onUpdateSectionConfig(section, { title: tempTitle });
    setEditingTitle(false);
  };

  const handleSubtitleSave = () => {
    onUpdateSectionConfig(section, { subtitle: tempSubtitle });
    setEditingSubtitle(false);
  };

  const handleTitleCancel = () => {
    setTempTitle(sectionConfig.title);
    setEditingTitle(false);
  };

  const handleSubtitleCancel = () => {
    setTempSubtitle(sectionConfig.subtitle);
    setEditingSubtitle(false);
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Sort fields by order, then by fieldPath for consistency
  const sortedFields = [...fields].sort((a, b) => {
    const orderA = a.field.order || 999;
    const orderB = b.field.order || 999;
    if (orderA !== orderB) return orderA - orderB;
    return a.fieldPath.localeCompare(b.fieldPath);
  });

  const fieldIds = sortedFields.map(f => f.fieldPath);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "bg-white rounded-lg border border-gray-200 overflow-hidden",
        isDragging && "shadow-lg"
      )}
    >
      <div className="px-4 py-3">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3 flex-1">
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600"
            >
              <GripVertical className="h-4 w-4" />
            </div>
            <button
              onClick={() => onToggleSection(section)}
              className="flex items-center gap-3 hover:bg-gray-50 rounded px-2 py-1 transition-colors"
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-500" />
              )}
              <Badge variant="secondary" className="text-xs">
                {enabledCount}/{fields.length}
              </Badge>
            </button>
          </div>
          <div className="flex items-center gap-3">
            {enabledCount === fields.length && (
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            )}
            <div className="flex items-center gap-2 mr-2">
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => fields.forEach(f => onUpdateFieldSetting(f.fieldPath, { enabled: true }))}
              >
                Show All
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => fields.forEach(f => onUpdateFieldSetting(f.fieldPath, { enabled: false }))}
              >
                Hide All
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium">
                {isSectionVisible ? 'Show' : 'Hide'}
              </Label>
              <Switch
                checked={isSectionVisible}
                onCheckedChange={(checked) => onUpdateSectionVisibility(section, checked)}
                className="data-[state=checked]:bg-blue-600 scale-75"
              />
            </div>
          </div>
        </div>

        {/* Editable Title */}
        <div className="mb-2">
          {editingTitle ? (
            <div className="flex items-center gap-2">
              <Input
                value={tempTitle}
                onChange={(e) => setTempTitle(e.target.value)}
                className="flex-1"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleTitleSave();
                  if (e.key === 'Escape') handleTitleCancel();
                }}
              />
              <Button size="sm" onClick={handleTitleSave} className="px-2">
                <CheckCircle2 className="h-3 w-3" />
              </Button>
              <Button size="sm" variant="ghost" onClick={handleTitleCancel} className="px-2">
                <X className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <div 
              className="flex items-center gap-2 group cursor-pointer hover:bg-gray-50 rounded px-2 py-1 transition-colors"
              onClick={() => setEditingTitle(true)}
            >
              <h4 className="font-medium text-gray-900 flex-1">{sectionConfig.title}</h4>
              <Edit2 className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          )}
        </div>

        {/* Editable Subtitle */}
        <div className="mb-3">
          {editingSubtitle ? (
            <div className="flex items-start gap-2">
              <Textarea
                value={tempSubtitle}
                onChange={(e) => setTempSubtitle(e.target.value)}
                className="flex-1 min-h-[60px]"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.ctrlKey) handleSubtitleSave();
                  if (e.key === 'Escape') handleSubtitleCancel();
                }}
              />
              <div className="flex flex-col gap-1">
                <Button size="sm" onClick={handleSubtitleSave} className="px-2">
                  <CheckCircle2 className="h-3 w-3" />
                </Button>
                <Button size="sm" variant="ghost" onClick={handleSubtitleCancel} className="px-2">
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ) : (
            <div 
              className="group cursor-pointer hover:bg-gray-50 rounded px-2 py-1 transition-colors"
              onClick={() => setEditingSubtitle(true)}
            >
              <div className="flex items-start gap-2">
                <p className="text-sm text-gray-600 flex-1">{sectionConfig.subtitle}</p>
                <Edit2 className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity mt-0.5" />
              </div>
            </div>
          )}
        </div>

        {/* Display Type Configuration */}
        <div className="flex items-center gap-4 p-2 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2">
            <Label className="text-xs font-medium text-gray-700">Display as:</Label>
            <Select
              value={sectionConfig.displayType}
              onValueChange={(value: 'page' | 'section') => 
                onUpdateSectionConfig(section, { displayType: value })
              }
            >
              <SelectTrigger className="h-7 text-xs w-28 whitespace-nowrap">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="page">New Page</SelectItem>
                <SelectItem value="section">Section</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs font-medium text-gray-700">Order:</Label>
            <Input
              type="number"
              value={sectionConfig.order}
              onChange={(e) => 
                onUpdateSectionConfig(section, { order: parseInt(e.target.value) || 1 })
              }
              className="h-7 text-xs w-16"
              min="1"
            />
          </div>
          {sectionConfig.displayType === 'page' ? (
            <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
              Creates new page
            </Badge>
          ) : (
            <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
              Part of previous page
            </Badge>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-gray-200">
          <DndContext
            sensors={[pointerSensor]}
            collisionDetection={closestCenter}
            onDragEnd={(event) => onHandleFieldDragEnd(section, event)}
          >
            <SortableContext items={fieldIds} strategy={verticalListSortingStrategy}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4">
                {sortedFields.map(({ fieldPath, field }) => (
                  <SortableField
                    key={fieldPath}
                    fieldPath={fieldPath}
                    field={field}
                    onUpdateFieldSetting={onUpdateFieldSetting}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
}

export const MerchantApplicationSettings = forwardRef<
  MerchantApplicationSettingsRef,
  MerchantApplicationSettingsProps
>(({ onSettingsChange, onSave }, ref) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['Personal Information', 'Processing History']));
  const [showOnlyEnabled, setShowOnlyEnabled] = useState(false);
  const [fieldFilter, setFieldFilter] = useState('');

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const [settings, setSettings] = useState<MerchantApplicationSettingsType>({
    sources: {},  // Will be populated dynamically from applicationSources collection
    defaultSource: '',  // Will be set to first source ID
    trackingEnabled: true,
  });
  const [selectedSource, setSelectedSource] = useState<ApplicationSource>('');  // Will be set dynamically
  const [bankingPartners, setBankingPartners] = useState<BankingPartner[]>([]);
  const [applicationSources, setApplicationSources] = useState<ApplicationSourceType[]>([]);

  useEffect(() => {
    const loadPartners = async () => {
      try {
        const partners = await bankingPartnerService.getAllBankingPartners();
        setBankingPartners(partners);
      } catch (error) {
        console.error('Failed to load banking partners', error);
      }
    };
    loadPartners();
  }, []);

  useEffect(() => {
    const loadSources = async () => {
      try {
        const sources = await applicationSourceService.getActiveApplicationSources();
        setApplicationSources(sources);

        // Set the first source as selected if we don't have a selected source yet
        if (sources.length > 0 && !selectedSource) {
          setSelectedSource(sources[0].id as ApplicationSource);
        }
      } catch (error) {
        console.error('Failed to load application sources', error);
      }
    };
    loadSources();
  }, []);

  useEffect(() => {
    onSettingsChange?.(hasChanges);
  }, [hasChanges, onSettingsChange]);

  const loadSettings = useCallback(async () => {
    if (!user?.companyId) return;

    try {
      setLoading(true);
      // Load settings from GLOBAL collection
      const settingsDoc = await getDoc(
        doc(db, 'merchantApplicationSettings', 'config')
      );

      if (settingsDoc.exists()) {
        const data = settingsDoc.data() as MerchantApplicationSettingsType;

        // Migrate name-based keys to ID-based keys
        let needsIdMigration = false;
        if (data.sources && applicationSources.length > 0) {
          // Check if any source key is a name instead of an ID
          const sourceKeys = Object.keys(data.sources);
          const hasNameKeys = sourceKeys.some(key =>
            ['InvoiceBug', 'Direct', 'Partner', 'Referral', 'Other'].includes(key)
          );

          if (hasNameKeys) {
            console.log('🔄 Migrating name-based keys to ID-based keys...');
            needsIdMigration = true;
            const migratedSources: Record<string, any> = {};

            // Map old name-based keys to new ID-based keys
            sourceKeys.forEach(oldKey => {
              // Find matching application source by name
              const matchingSource = applicationSources.find(
                s => s.name === oldKey || s.displayName === oldKey
              );

              if (matchingSource) {
                console.log(`  Mapping "${oldKey}" → "${matchingSource.id}"`);
                // Use ID as new key
                migratedSources[matchingSource.id] = data.sources[oldKey];
              } else {
                // Keep unmapped sources (custom ones) with their original keys
                console.log(`  Keeping unmapped source: "${oldKey}"`);
                migratedSources[oldKey] = data.sources[oldKey];
              }
            });

            data.sources = migratedSources;

            // Update defaultSource to ID if it's currently a name
            if (data.defaultSource && ['InvoiceBug', 'Direct', 'Partner', 'Referral', 'Other'].includes(data.defaultSource)) {
              const matchingSource = applicationSources.find(
                s => s.name === data.defaultSource || s.displayName === data.defaultSource
              );
              if (matchingSource) {
                console.log(`  Default source: "${data.defaultSource}" → "${matchingSource.id}"`);
                data.defaultSource = matchingSource.id;
              }
            }
          }
        }

        // Simple safety check and migration
        if (!data.sources) {
          // If no sources exist, create default ones dynamically from applicationSources
          const defaultSourcesMap: Record<string, ApplicationSourceSettings> = {};
          applicationSources.forEach(appSource => {
            defaultSourcesMap[appSource.id] = createDefaultSourceSettings(
              appSource.name,
              appSource.displayName,
              false
            );
          });

          const defaultSettings: MerchantApplicationSettingsType = {
            sources: defaultSourcesMap,
            defaultSource: applicationSources[0]?.id || '',  // Use first source ID
            trackingEnabled: true,
          };
          setSettings(defaultSettings);
        } else {
          // Migrate legacy Individual Information fields to Beneficial Owners
          const migrated = { ...data, sources: { ...data.sources } } as MerchantApplicationSettingsType;
          let structureMigrated = false; // Track actual structure migration only

          Object.entries(migrated.sources).forEach(([sourceId, sourceCfg]) => {
            const fieldSettings = { ...(sourceCfg.fieldSettings || {}) } as Record<string, FieldSetting>;
            const updates: Array<{ oldKey: string; newKey: string }> = [
              { oldKey: 'individualInfo.ssn', newKey: 'beneficialOwners.ssn' },
              { oldKey: 'individualInfo.driverLicense', newKey: 'beneficialOwners.driverLicense' },
              { oldKey: 'individualInfo.dateOfBirth', newKey: 'beneficialOwners.dateOfBirth' },
            ];

            updates.forEach(({ oldKey, newKey }) => {
              const legacy = fieldSettings[oldKey] as FieldSetting | undefined;
              if (legacy) {
                // Create the new field using defaults and preserve enabled/required
                const defaultDef = DEFAULT_FIELD_DEFINITIONS[newKey] || {};
                fieldSettings[newKey] = {
                  fieldName: newKey.split('.').pop() || '',
                  fieldPath: newKey,
                  label: defaultDef.label || legacy.label,
                  section: 'Beneficial Owners',
                  enabled: legacy.enabled,
                  required: legacy.required,
                  order: defaultDef.order ?? legacy.order,
                  placeholder: defaultDef.placeholder || legacy.placeholder,
                  helpText: defaultDef.helpText || legacy.helpText,
                  validation: legacy.validation,
                } as FieldSetting;
                delete fieldSettings[oldKey];
                structureMigrated = true;
              }
            });

            Object.entries(DEFAULT_FIELD_DEFINITIONS).forEach(([fieldPath, definition]) => {
              if (!fieldSettings[fieldPath]) {
                fieldSettings[fieldPath] = {
                  fieldName: fieldPath.split('.').pop() || '',
                  fieldPath,
                  label: definition.label || fieldPath.split('.').pop() || '',
                  section: definition.section || 'Other',
                  enabled: true,
                  required: true,
                  order: definition.order,
                  placeholder: definition.placeholder,
                  helpText: definition.helpText,
                  linkEnabled: definition.linkEnabled,
                  validation: definition.validation,
                  defaultValue: definition.defaultValue,
                } as FieldSetting;
                // Don't mark as changed - just add missing fields in memory
              } else if (
                definition.defaultValue !== undefined &&
                fieldSettings[fieldPath].defaultValue === undefined
              ) {
                fieldSettings[fieldPath] = {
                  ...fieldSettings[fieldPath],
                  defaultValue: definition.defaultValue,
                };
                // Don't mark as changed - just update default values in memory
              }
            });

            // Migrate legacy structure to nested structure ONLY if old structure exists
            if (migrated.defaultBankingPartnerId && migrated.industryClassification) {
              // Check if this source has ONLY legacy structure (no nested structure yet)
              const hasLegacyOnly = sourceCfg.fieldSettings && !sourceCfg.bankingPartnerConfigurations;

              if (hasLegacyOnly) {
                // MIGRATION NEEDED: Convert legacy to nested
                structureMigrated = true;

                // Create classification settings
                const classificationSettings: ClassificationSettings = {
                  fieldSettings,
                  documentRequirements: sourceCfg.documentRequirements || [...DEFAULT_DOCUMENT_REQUIREMENTS],
                  sectionVisibility: {
                    [APPLICATION_SECTIONS.PERSONAL_INFO]: true,
                    [APPLICATION_SECTIONS.CORPORATION_INFO]: true,
                    [APPLICATION_SECTIONS.PROCESSING_HISTORY]: true,
                    [APPLICATION_SECTIONS.BENEFICIAL_OWNERS]: true,
                    [APPLICATION_SECTIONS.BANK_DETAILS]: true,
                    [APPLICATION_SECTIONS.DOCUMENTATION]: true,
                    ...(sourceCfg.sectionVisibility || {}),
                  },
                  sectionOrder: sourceCfg.sectionOrder || [...DEFAULT_SECTION_ORDER],
                  sectionConfigs: {
                    ...DEFAULT_SECTION_CONFIGS,
                    ...(sourceCfg.sectionConfigs || {}),
                  },
                };

                // Create nested structure from legacy data
                const bankingPartnerConfigurations: Record<string, BankingPartnerConfiguration> = {
                  [migrated.defaultBankingPartnerId]: {
                    'low-risk': classificationSettings,
                    'high-risk': classificationSettings,
                  },
                };

                // Remove legacy fields and use ONLY nested structure
                migrated.sources[sourceId] = {
                  source: sourceCfg.source,
                  displayName: sourceCfg.displayName,
                  defaultPipelineStatus: sourceCfg.defaultPipelineStatus,
                  isCustom: sourceCfg.isCustom,
                  customQuestions: sourceCfg.customQuestions,
                  bankingPartnerConfigurations,
                };
              } else {
                // Already has nested structure, keep it as is (no changes)
                migrated.sources[sourceId] = sourceCfg;
              }
            } else {
              // No banking partner/classification set, keep as is
              migrated.sources[sourceId] = sourceCfg;
            }
          });

          setSettings(migrated);

          // Set selected source to default source from settings if it exists
          if (migrated.defaultSource) {
            setSelectedSource(migrated.defaultSource as ApplicationSource);
          } else if (applicationSources.length > 0) {
            setSelectedSource(applicationSources[0].id as ApplicationSource);
          }

          // Persist migration immediately ONLY if structure was actually migrated
          if (structureMigrated || needsIdMigration) {
            try {
              // Sanitize to remove undefined values before saving
              const sanitize = <T,>(obj: T): T => {
                if (obj === null || obj === undefined) {
                  return null as unknown as T;
                }
                if (Array.isArray(obj)) {
                  return obj.map((item) => sanitize(item)) as unknown as T;
                }
                if (typeof obj === 'object') {
                  const sanitized = Object.entries(obj as Record<string, unknown>).reduce<Record<string, unknown>>((acc, [key, value]) => {
                    if (value !== undefined) {
                      acc[key] = sanitize(value);
                    }
                    return acc;
                  }, {});
                  return sanitized as unknown as T;
                }
                return obj;
              };

              // Save to GLOBAL collection
              await setDoc(
                doc(db, 'merchantApplicationSettings', 'config'),
                sanitize(migrated)
              );
              const migrationMessage = needsIdMigration && structureMigrated
                ? 'Settings migrated: ID-based keys and nested structure'
                : needsIdMigration
                ? 'Settings migrated: Now using ID-based source keys'
                : 'Settings migrated: Updated to nested structure';

              toast({
                title: 'Settings Migrated',
                description: migrationMessage,
              });
            } catch (e) {
              console.warn('Failed to persist migration; will rely on manual save.', e);
            }
          }
        }
      } else {
        // Initialize with default settings dynamically from applicationSources
        const defaultSourcesMap: Record<string, ApplicationSourceSettings> = {};
        applicationSources.forEach(appSource => {
          defaultSourcesMap[appSource.id] = createDefaultSourceSettings(
            appSource.name,
            appSource.displayName,
            false
          );
        });

        const defaultSettings: MerchantApplicationSettingsType = {
          sources: defaultSourcesMap,
          defaultSource: applicationSources[0]?.id || '',
          trackingEnabled: true,
        };
        setSettings(defaultSettings);

        // Set selected source to first available source from loaded sources
        if (applicationSources.length > 0) {
          setSelectedSource(applicationSources[0].id as ApplicationSource);
        }
      }
    } catch (error) {
      console.error('Error loading merchant application settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load settings',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [user?.companyId, toast, applicationSources]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Function to sanitize data for Firestore - removes undefined values
  const sanitizeForFirestore = useCallback(<T,>(obj: T): T => {
    if (obj === null || obj === undefined) {
      return null as unknown as T;
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => sanitizeForFirestore(item)) as unknown as T;
    }

    if (typeof obj === 'object') {
      const sanitized = Object.entries(obj as Record<string, unknown>).reduce<Record<string, unknown>>((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = sanitizeForFirestore(value);
        }
        return acc;
      }, {});
      return sanitized as unknown as T;
    }

    return obj;
  }, []);

  const saveSettings = useCallback(async () => {
    if (!user?.companyId) return;

    try {
      setSaving(true);

      // Check if banking partner and industry classification are set
      if (!settings.defaultBankingPartnerId || !settings.industryClassification) {
        toast({
          title: 'Missing Configuration',
          description: 'Please select a Default Banking Partner and Industry Classification before saving',
          variant: 'destructive',
        });
        setSaving(false);
        return;
      }

      // Validate: Hidden + required fields must have default
      const missingDefaults: string[] = [];
      Object.entries(settings.sources).forEach(([, cfg]) => {
        // Check nested structure first
        const bankingPartnerConfig = cfg.bankingPartnerConfigurations?.[cfg.bankingPartnerId || settings.defaultBankingPartnerId!];
        const classificationSettings = bankingPartnerConfig?.[settings.industryClassification!];
        const fieldSettings = classificationSettings?.fieldSettings || cfg.fieldSettings || {};

        Object.values(fieldSettings).forEach((f) => {
          if (!f.enabled && f.required && (f.defaultValue === undefined || f.defaultValue === '')) {
            missingDefaults.push(`${cfg.displayName}: ${f.label}`);
          }
        });
      });
      if (missingDefaults.length > 0) {
        toast({
          title: 'Missing Defaults for Hidden Required Fields',
          description: `Please set defaults for: ${missingDefaults.slice(0,5).join(', ')}${missingDefaults.length>5 ? '…' : ''}`,
          variant: 'destructive',
        });
        setSaving(false);
        return;
      }

      // Clean up settings: Remove legacy fields and keep only nested structure
      const cleanedSettings: MerchantApplicationSettingsType = {
        ...settings,
        sources: Object.entries(settings.sources).reduce((acc, [sourceId, sourceCfg]) => {
          // Keep only the nested structure, remove legacy fields
          acc[sourceId] = {
            source: sourceCfg.source,
            displayName: sourceCfg.displayName,
            defaultPipelineStatus: sourceCfg.defaultPipelineStatus,
            isCustom: sourceCfg.isCustom,
            customQuestions: sourceCfg.customQuestions,
            bankingPartnerId: sourceCfg.bankingPartnerId,        // Per-source banking partner
            bankingPartnerConfigurations: sourceCfg.bankingPartnerConfigurations,
            // DO NOT include: fieldSettings, documentRequirements, sectionVisibility, sectionConfigs, bankingPartnerApiKey (these are in nested bankingPartnerConfigurations)
            // NOTE: sectionOrder IS included in the nested bankingPartnerConfigurations structure
          };
          return acc;
        }, {} as Record<string, ApplicationSourceSettings>),
      };

      // Sanitize settings to remove any undefined values
      const sanitizedSettings = sanitizeForFirestore(cleanedSettings);

      // Save to GLOBAL collection
      await setDoc(
        doc(db, 'merchantApplicationSettings', 'config'),
        sanitizedSettings
      );

      toast({
        title: 'Success',
        description: 'Global settings saved: sources → banking partner → industry classification',
      });

      setHasChanges(false);
      onSave?.();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: 'Error',
        description: `Failed to save settings: ${error.message}`,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  }, [user?.companyId, settings, toast, onSave, sanitizeForFirestore]);

  // Must be after saveSettings is defined
  useImperativeHandle(ref, () => ({
    save: saveSettings,
  }), [saveSettings]);

  const updateFieldSetting = (fieldPath: string, updates: Partial<FieldSetting>) => {
    setHasChanges(true);
    setSettings((prev) => {
      const sourceSettings = prev.sources[selectedSource];
      const currentSettings = getCurrentClassificationSettings(
        sourceSettings,
        sourceSettings.bankingPartnerId || prev.defaultBankingPartnerId,
        prev.industryClassification
      );

      if (!currentSettings) return prev;

      // Update the specific field
      const updatedFieldSettings = {
        ...currentSettings.fieldSettings,
        [fieldPath]: {
          ...currentSettings.fieldSettings[fieldPath],
          ...updates,
        },
      };

      // Prepare classification settings update
      const classificationUpdates: Partial<ClassificationSettings> = {
        fieldSettings: updatedFieldSettings,
      };

      // Check if all fields in a section are disabled and update section visibility
      if ('enabled' in updates) {
        const sections = new Set<string>();

        // Get all sections (excluding personal address)
        Object.entries(updatedFieldSettings).forEach(([fp, field]) => {
          if (field.section && !fp.includes('personalAddress')) sections.add(field.section);
        });

        // Check each section
        const updatedSectionVisibility = { ...(currentSettings.sectionVisibility || {}) };
        sections.forEach(section => {
          const sectionFields = Object.entries(updatedFieldSettings)
            .filter(([fp, field]) => field.section === section && !fp.includes('personalAddress'))
            .map(([, field]) => field);
          const allDisabled = sectionFields.every(field => !field.enabled);

          if (allDisabled && updatedSectionVisibility[section] !== false) {
            updatedSectionVisibility[section] = false;
          }
        });

        classificationUpdates.sectionVisibility = updatedSectionVisibility;
      }

      // Update the nested structure
      const updatedSourceSettings = updateNestedClassificationSettings(
        sourceSettings,
        sourceSettings.bankingPartnerId || prev.defaultBankingPartnerId,
        prev.industryClassification,
        classificationUpdates
      );

      return {
        ...prev,
        sources: {
          ...prev.sources,
          [selectedSource]: updatedSourceSettings,
        },
      };
    });
  };

  const updateSectionVisibility = (section: string, visible: boolean) => {
    setHasChanges(true);
    setSettings((prev) => {
      const sourceSettings = prev.sources[selectedSource];
      const currentSettings = getCurrentClassificationSettings(
        sourceSettings,
        sourceSettings.bankingPartnerId || prev.defaultBankingPartnerId,
        prev.industryClassification
      );

      if (!currentSettings) return prev;

      const updatedSourceSettings = updateNestedClassificationSettings(
        sourceSettings,
        sourceSettings.bankingPartnerId || prev.defaultBankingPartnerId,
        prev.industryClassification,
        {
          sectionVisibility: {
            ...currentSettings.sectionVisibility,
            [section]: visible,
          },
        }
      );

      return {
        ...prev,
        sources: {
          ...prev.sources,
          [selectedSource]: updatedSourceSettings,
        },
      };
    });
  };

  const _updateSectionOrder = (newOrder: string[]) => {
    setHasChanges(true);
    setSettings((prev) => {
      const sourceSettings = prev.sources[selectedSource];
      const currentSettings = getCurrentClassificationSettings(
        sourceSettings,
        sourceSettings.bankingPartnerId || prev.defaultBankingPartnerId,
        prev.industryClassification
      );

      if (!currentSettings) return prev;

      const updatedSourceSettings = updateNestedClassificationSettings(
        sourceSettings,
        sourceSettings.bankingPartnerId || prev.defaultBankingPartnerId,
        prev.industryClassification,
        {
          sectionOrder: newOrder,
        }
      );

      return {
        ...prev,
        sources: {
          ...prev.sources,
          [selectedSource]: updatedSourceSettings,
        },
      };
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setHasChanges(true);
      setSettings((prev) => {
        const sourceSettings = prev.sources[selectedSource];
        const currentSettings = getCurrentClassificationSettings(
          sourceSettings,
          sourceSettings.bankingPartnerId || prev.defaultBankingPartnerId,
          prev.industryClassification
        );

        if (!currentSettings) return prev;

        const currentOrder = currentSettings.sectionOrder || DEFAULT_SECTION_ORDER;
        const oldIndex = currentOrder.indexOf(active.id as string);
        const newIndex = currentOrder.indexOf(over.id as string);

        if (oldIndex !== -1 && newIndex !== -1) {
          const newOrder = arrayMove(currentOrder, oldIndex, newIndex);

          // Also update sectionConfigs to keep order property in sync
          const updatedSectionConfigs = { ...currentSettings.sectionConfigs };
          newOrder.forEach((section, index) => {
            if (updatedSectionConfigs[section]) {
              updatedSectionConfigs[section] = {
                ...updatedSectionConfigs[section],
                order: index + 1, // 1-based indexing
              };
            }
          });

          const updatedSourceSettings = updateNestedClassificationSettings(
            sourceSettings,
            sourceSettings.bankingPartnerId || prev.defaultBankingPartnerId,
            prev.industryClassification,
            {
              sectionOrder: newOrder,
              sectionConfigs: updatedSectionConfigs,
            }
          );

          return {
            ...prev,
            sources: {
              ...prev.sources,
              [selectedSource]: updatedSourceSettings,
            },
          };
        }

        return prev;
      });
    }
  };

  const handleFieldDragEnd = (section: string, event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const activeFieldPath = active.id as string;
      const overFieldPath = over.id as string;

      // Get all fields in this section (excluding personal address)
      const sectionFields = Object.entries(workingSettings.fieldSettings).filter(
        ([fieldPath, field]) => field.section === section && !fieldPath.includes('personalAddress')
      );

      // Sort by current order
      const sortedSectionFields = sectionFields.sort(([aPath, aField], [bPath, bField]) => {
        const orderA = aField.order || 999;
        const orderB = bField.order || 999;
        if (orderA !== orderB) return orderA - orderB;
        return aPath.localeCompare(bPath);
      });

      const activeIndex = sortedSectionFields.findIndex(([path]) => path === activeFieldPath);
      const overIndex = sortedSectionFields.findIndex(([path]) => path === overFieldPath);

      if (activeIndex !== -1 && overIndex !== -1) {
        const reorderedFields = arrayMove(sortedSectionFields, activeIndex, overIndex);

        // Update order numbers for all fields in this section
        setHasChanges(true);
        setSettings((prev) => {
          const sourceSettings = prev.sources[selectedSource];
          const currentSettings = getCurrentClassificationSettings(
            sourceSettings,
            sourceSettings.bankingPartnerId || prev.defaultBankingPartnerId,
            prev.industryClassification
          );

          if (!currentSettings) return prev;

          // Create updated field settings with new order
          const updatedFieldSettings = { ...currentSettings.fieldSettings };
          reorderedFields.forEach(([fieldPath], index) => {
            if (updatedFieldSettings[fieldPath]) {
              updatedFieldSettings[fieldPath] = {
                ...updatedFieldSettings[fieldPath],
                order: index + 1,
              };
            }
          });

          const updatedSourceSettings = updateNestedClassificationSettings(
            sourceSettings,
            prev.defaultBankingPartnerId,
            prev.industryClassification,
            {
              fieldSettings: updatedFieldSettings,
            }
          );

          return {
            ...prev,
            sources: {
              ...prev.sources,
              [selectedSource]: updatedSourceSettings,
            },
          };
        });
      }
    }
  };

  const updateDocumentRequirement = (index: number, updates: Partial<DocumentRequirement>) => {
    setHasChanges(true);
    setSettings((prev) => {
      const sourceSettings = prev.sources[selectedSource];
      const currentSettings = getCurrentClassificationSettings(
        sourceSettings,
        sourceSettings.bankingPartnerId || prev.defaultBankingPartnerId,
        prev.industryClassification
      );

      if (!currentSettings) return prev;

      const newRequirements = [...currentSettings.documentRequirements];
      newRequirements[index] = { ...newRequirements[index], ...updates };

      const updatedSourceSettings = updateNestedClassificationSettings(
        sourceSettings,
        sourceSettings.bankingPartnerId || prev.defaultBankingPartnerId,
        prev.industryClassification,
        {
          documentRequirements: newRequirements,
        }
      );

      return {
        ...prev,
        sources: {
          ...prev.sources,
          [selectedSource]: updatedSourceSettings,
        },
      };
    });
  };

  const addDocumentRequirement = () => {
    setHasChanges(true);
    const newRequirement: DocumentRequirement = {
      documentType: `custom_${Date.now()}`,
      label: 'New Document',
      enabled: false,
      required: false,
      acceptedFormats: ['pdf', 'jpg', 'jpeg', 'png'],
      maxFileSize: 5,
    };

    setSettings((prev) => {
      const sourceSettings = prev.sources[selectedSource];
      const currentSettings = getCurrentClassificationSettings(
        sourceSettings,
        sourceSettings.bankingPartnerId || prev.defaultBankingPartnerId,
        prev.industryClassification
      );

      if (!currentSettings) return prev;

      const updatedSourceSettings = updateNestedClassificationSettings(
        sourceSettings,
        sourceSettings.bankingPartnerId || prev.defaultBankingPartnerId,
        prev.industryClassification,
        {
          documentRequirements: [...currentSettings.documentRequirements, newRequirement],
        }
      );

      return {
        ...prev,
        sources: {
          ...prev.sources,
          [selectedSource]: updatedSourceSettings,
        },
      };
    });
  };

  const removeDocumentRequirement = (index: number) => {
    setHasChanges(true);
    setSettings((prev) => {
      const sourceSettings = prev.sources[selectedSource];
      const currentSettings = getCurrentClassificationSettings(
        sourceSettings,
        sourceSettings.bankingPartnerId || prev.defaultBankingPartnerId,
        prev.industryClassification
      );

      if (!currentSettings) return prev;

      const newRequirements = currentSettings.documentRequirements.filter(
        (_, i) => i !== index
      );

      const updatedSourceSettings = updateNestedClassificationSettings(
        sourceSettings,
        sourceSettings.bankingPartnerId || prev.defaultBankingPartnerId,
        prev.industryClassification,
        {
          documentRequirements: newRequirements,
        }
      );

      return {
        ...prev,
        sources: {
          ...prev.sources,
          [selectedSource]: updatedSourceSettings,
        },
      };
    });
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const expandAllSections = () => {
    const sections = Object.keys(settings.sources[selectedSource].sectionConfigs || DEFAULT_SECTION_CONFIGS);
    setExpandedSections(new Set(sections));
  };

  const collapseAllSections = () => {
    setExpandedSections(new Set());
  };

  const updateSectionConfig = (section: string, updates: Partial<SectionConfig>) => {
    setHasChanges(true);
    setSettings(prev => {
      const sourceSettings = prev.sources[selectedSource];
      const currentSettings = getCurrentClassificationSettings(
        sourceSettings,
        sourceSettings.bankingPartnerId || prev.defaultBankingPartnerId,
        prev.industryClassification
      );

      if (!currentSettings) return prev;

      // Start with current sectionOrder
      const updatedSectionOrder = [...(currentSettings.sectionOrder || DEFAULT_SECTION_ORDER)];
      const updatedSectionConfigs = {
        ...currentSettings.sectionConfigs,
        ...DEFAULT_SECTION_CONFIGS,
      };

      // If order was updated, move the section in the array
      if (updates.order !== undefined) {
        const newOrder = updates.order;
        const currentIndex = updatedSectionOrder.indexOf(section);

        if (currentIndex !== -1) {
          // Remove from current position
          updatedSectionOrder.splice(currentIndex, 1);
          // Insert at new position (order is 1-based, array is 0-based)
          const newIndex = Math.max(0, Math.min(newOrder - 1, updatedSectionOrder.length));
          updatedSectionOrder.splice(newIndex, 0, section);

          // Update ALL section configs based on their position in the array
          updatedSectionOrder.forEach((sec, index) => {
            updatedSectionConfigs[sec] = {
              ...updatedSectionConfigs[sec],
              order: index + 1, // 1-based indexing
            };
          });
        }
      }

      // Apply the specific updates for this section
      updatedSectionConfigs[section] = {
        ...updatedSectionConfigs[section],
        ...updates,
      };

      const updatedSourceSettings = updateNestedClassificationSettings(
        sourceSettings,
        sourceSettings.bankingPartnerId || prev.defaultBankingPartnerId,
        prev.industryClassification,
        {
          sectionConfigs: updatedSectionConfigs,
          sectionOrder: updatedSectionOrder,
        }
      );

      return {
        ...prev,
        sources: {
          ...prev.sources,
          [selectedSource]: updatedSourceSettings,
        },
      };
    });
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Get or create source settings with defaults
  let currentSourceSettings = settings.sources[selectedSource];

  // If source settings don't exist (e.g., newly added source), initialize with defaults
  if (!currentSourceSettings) {
    console.log('Source settings not found for:', selectedSource, '- initializing with defaults');

    // Find the display name from application sources
    const applicationSource = applicationSources.find(s => s.id === selectedSource);
    const displayName = applicationSource?.displayName || selectedSource;

    // Create default settings for this source
    currentSourceSettings = createDefaultSourceSettings(
      selectedSource as ApplicationSource,
      displayName,
      true // Mark as custom since it's being created now
    );

    // Update settings state with the new source
    setSettings(prev => ({
      ...prev,
      sources: {
        ...prev.sources,
        [selectedSource]: currentSourceSettings,
      },
    }));
  }

  console.log('Selected source:', selectedSource);
  console.log('Current source settings:', currentSourceSettings);

  // Get current classification settings (supports both nested and legacy structures)
  // Use per-source bankingPartnerId first, fallback to global defaultBankingPartnerId
  const currentClassificationSettings = getCurrentClassificationSettings(
    currentSourceSettings,
    currentSourceSettings.bankingPartnerId || settings.defaultBankingPartnerId,
    settings.industryClassification
  );

  // For backwards compatibility, create a merged view that includes both legacy and nested data
  const workingSettings = currentClassificationSettings || {
    fieldSettings: currentSourceSettings.fieldSettings || {},
    documentRequirements: currentSourceSettings.documentRequirements || [],
    sectionVisibility: currentSourceSettings.sectionVisibility,
    sectionOrder: currentSourceSettings.sectionOrder,
    sectionConfigs: currentSourceSettings.sectionConfigs,
  };

  return (
    <div className="space-y-6">
      {/* Modern Control Panel */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50/50 rounded-xl p-6 border border-blue-200/30">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {/* Source Tracking */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <Database className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Source Tracking</Label>
                  <p className="text-xs text-gray-600">Track application origins</p>
                </div>
              </div>
              <Switch
                checked={settings.trackingEnabled}
                onCheckedChange={(checked) => {
                  setHasChanges(true);
                  setSettings((prev) => ({ ...prev, trackingEnabled: checked }));
                }}
                className="data-[state=checked]:bg-blue-600"
              />
            </div>
          </div>

          {/* Configure For */}
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-2">
              <Zap className="h-4 w-4 text-amber-500" />
              Configure Settings For
            </Label>
            <Select
              value={selectedSource}
              onValueChange={(value) => setSelectedSource(value as ApplicationSource)}
            >
              <SelectTrigger className="h-9 bg-white">
                <SelectValue placeholder="Select a source" />
              </SelectTrigger>
              <SelectContent>
                {applicationSources.map((source) => (
                  <SelectItem key={source.id} value={source.id}>
                    {source.displayName}
                  </SelectItem>
                ))}
                {applicationSources.length === 0 && (
                  <div className="py-2 px-3 text-sm text-gray-500">No sources found</div>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Default Banking Partner */}
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-2">
              <Shield className="h-4 w-4 text-gray-500" />
              Banking Partner
            </Label>
            <Select
              value={currentSourceSettings.bankingPartnerId || ''}
              onValueChange={(value) => {
                setHasChanges(true);
                setSettings((prev) => ({
                  ...prev,
                  sources: {
                    ...prev.sources,
                    [selectedSource]: {
                      ...prev.sources[selectedSource],
                      bankingPartnerId: value,
                    },
                  },
                }));
              }}
            >
              <SelectTrigger className="h-9 bg-white">
                <SelectValue placeholder="Select a partner" />
              </SelectTrigger>
              <SelectContent>
                {bankingPartners.map((partner) => (
                  <SelectItem key={partner.id} value={partner.id}>
                    {partner.name}
                  </SelectItem>
                ))}
                {bankingPartners.length === 0 && (
                  <div className="py-2 px-3 text-sm text-gray-500">No partners found</div>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Industry Classification */}
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-gray-500" />
              Industry Classification
            </Label>
            <Select
              value={settings.industryClassification || ''}
              onValueChange={(value: IndustryClassification) => {
                setHasChanges(true);
                setSettings((prev) => ({ ...prev, industryClassification: value }));
              }}
            >
              <SelectTrigger className="h-9 bg-white">
                <SelectValue placeholder="Select classification" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low-risk">
                  <div className="flex items-center gap-2">
                    <span>Low Risk</span>
                    <Badge variant="secondary" className="text-xs bg-green-100 text-green-700 border-0">
                      PayFac
                    </Badge>
                  </div>
                </SelectItem>
                <SelectItem value="high-risk">
                  <div className="flex items-center gap-2">
                    <span>High Risk</span>
                    <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-700 border-0">
                      Traditional
                    </Badge>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Modern Tabs */}
      <Tabs defaultValue="fields" className="space-y-0">
        <div className="flex items-center justify-between mb-6">
          <TabsList className="bg-gray-100 h-11 p-1">
            <TabsTrigger value="fields" className="data-[state=active]:bg-white data-[state=active]:shadow-sm px-6">
              <FileText className="h-4 w-4 mr-2" />
              Field Settings
            </TabsTrigger>
            <TabsTrigger value="documents" className="data-[state=active]:bg-white data-[state=active]:shadow-sm px-6">
              <Shield className="h-4 w-4 mr-2" />
              Document Requirements
            </TabsTrigger>
          </TabsList>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowOnlyEnabled(!showOnlyEnabled)}
              className={cn(
                "transition-colors",
                showOnlyEnabled && "bg-blue-50 border-blue-200 text-blue-700"
              )}
            >
              <Filter className="h-4 w-4 mr-2" />
              {showOnlyEnabled ? 'Showing Enabled' : 'Show All'}
            </Button>
{/* Reset to Defaults button hidden per request
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (confirm('Are you sure you want to reset to default settings? This will clear all custom configurations.')) {
                  // Reset to current default configuration dynamically
                  const defaultSourcesMap: Record<string, ApplicationSourceSettings> = {};
                  applicationSources.forEach(appSource => {
                    defaultSourcesMap[appSource.id] = createDefaultSourceSettings(
                      appSource.name,
                      appSource.displayName,
                      false
                    );
                  });

                  const defaultSettings: MerchantApplicationSettingsType = {
                    sources: defaultSourcesMap,
                    defaultSource: applicationSources[0]?.id || '',
                    trackingEnabled: true,
                  };

                  setSettings(defaultSettings);
                  setHasChanges(true);

                  toast({
                    title: 'Settings Reset',
                    description: 'Settings have been reset to current defaults. Save to persist changes.',
                  });
                }
              }}
              className="text-orange-600 hover:text-orange-700 border-orange-300 hover:border-orange-400"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Reset to Defaults
            </Button>
            */}
            <Button 
              onClick={saveSettings} 
              disabled={saving || !hasChanges}
              size="sm"
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-sm"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Field Settings Tab */}
        <TabsContent value="fields" className="mt-0">
          <div className="bg-gray-50/50 rounded-xl border border-gray-200/50 p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-amber-500" />
                Configure fields for {currentSourceSettings.displayName}
              </h3>
              <Badge variant="secondary" className="bg-blue-100 text-blue-700 border-0">
                {Object.entries(workingSettings.fieldSettings).filter(([fieldPath, f]) => !fieldPath.includes('personalAddress') && f.enabled).length} fields enabled
              </Badge>
            </div>
            <div className="flex items-center gap-3 mb-3 sticky top-16 z-10 bg-gray-50/50 py-2">
              <Input
                placeholder="Search fields..."
                value={fieldFilter}
                onChange={(e) => setFieldFilter(e.target.value)}
                className="max-w-sm"
              />
              {fieldFilter && (
                <Button variant="ghost" size="sm" onClick={() => setFieldFilter('')}>Clear</Button>
              )}
              <div className="ml-auto flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={expandAllSections}>
                  Expand All
                </Button>
                <Button variant="outline" size="sm" onClick={collapseAllSections}>
                  Collapse All
                </Button>
              </div>
            </div>

            {/* Draggable Sections */}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={workingSettings.sectionOrder || DEFAULT_SECTION_ORDER}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3">
                  {(() => {
                    // Group fields by section
                    const fieldsBySection = Object.entries(workingSettings.fieldSettings).reduce((acc, [fieldPath, field]) => {
                      // Filter out personal address field (deprecated)
                      if (fieldPath.includes('personalAddress')) return acc;
                      if (showOnlyEnabled && !field.enabled) return acc;
                      if (fieldFilter && !field.label.toLowerCase().includes(fieldFilter.toLowerCase())) return acc;
                      const section = field.section || 'Other';
                      if (!acc[section]) acc[section] = [];
                      acc[section].push({ fieldPath, field });
                      return acc;
                    }, {} as Record<string, Array<{ fieldPath: string; field: FieldSetting }>>);

                    // Sort sections according to custom order from sectionConfigs
                    const sectionConfigs = workingSettings.sectionConfigs || DEFAULT_SECTION_CONFIGS;
                    const sectionsWithFields = Object.keys(fieldsBySection);
                    const orderedSections = sectionsWithFields.sort((a, b) => {
                      const orderA = sectionConfigs[a]?.order || DEFAULT_SECTION_CONFIGS[a]?.order || 999;
                      const orderB = sectionConfigs[b]?.order || DEFAULT_SECTION_CONFIGS[b]?.order || 999;
                      return orderA - orderB;
                    });

                    return orderedSections.map((section) => {
                      const fields = fieldsBySection[section] || [];
                      const isExpanded = expandedSections.has(section);
                      const enabledCount = fields.filter(f => f.field.enabled).length;
                      const isSectionVisible = workingSettings.sectionVisibility?.[section] ?? true;
                      const sectionConfig = sectionConfigs[section] || DEFAULT_SECTION_CONFIGS[section] || {
                        title: section,
                        subtitle: '',
                        displayType: 'section',
                        order: 999,
                      };

                      return (
                        <SortableSection
                          key={section}
                          section={section}
                          fields={fields}
                          isExpanded={isExpanded}
                          enabledCount={enabledCount}
                          isSectionVisible={isSectionVisible}
                          sectionConfig={sectionConfig}
                          onToggleSection={toggleSection}
                          onUpdateSectionVisibility={updateSectionVisibility}
                          onUpdateFieldSetting={updateFieldSetting}
                          onHandleFieldDragEnd={handleFieldDragEnd}
                        onUpdateSectionConfig={updateSectionConfig}
                      />
                      );
                    });
                  })()}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </TabsContent>

        {/* Document Requirements Tab */}
        <TabsContent value="documents" className="mt-0">
          <div className="bg-gray-50/50 rounded-xl border border-gray-200/50 p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Shield className="h-4 w-4 text-indigo-500" />
                Configure document requirements for {currentSourceSettings.displayName}
              </h3>
              <Badge variant="secondary" className="bg-indigo-100 text-indigo-700 border-0">
                {workingSettings.documentRequirements.filter(d => d.enabled).length} documents required
              </Badge>
            </div>

            <div className="space-y-3">
              {workingSettings.documentRequirements.map((doc, index) => (
                <div
                  key={doc.documentType}
                  className={cn(
                    "bg-white rounded-lg border p-4 transition-all",
                    doc.enabled ? "border-indigo-200" : "border-gray-200"
                  )}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <Checkbox
                        checked={doc.enabled}
                        onCheckedChange={(checked) =>
                          updateDocumentRequirement(index, { enabled: checked as boolean })
                        }
                        className="mt-1"
                      />
                      <div className="flex-1 space-y-3">
                        <div>
                          <Input
                            value={doc.label}
                            onChange={(e) =>
                              updateDocumentRequirement(index, { label: e.target.value })
                            }
                            className={cn(
                              "font-medium",
                              !doc.enabled && "text-gray-500"
                            )}
                            disabled={!doc.enabled}
                          />
                          {doc.enabled && doc.description && (
                            <p className="text-xs text-gray-500 mt-1">{doc.description}</p>
                          )}
                        </div>

                        {doc.enabled && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label className="text-xs font-medium">Description</Label>
                              <Input
                                value={doc.description || ''}
                                onChange={(e) =>
                                  updateDocumentRequirement(index, { description: e.target.value })
                                }
                                placeholder="Brief description"
                                className="h-8 text-sm"
                              />
                            </div>

                            <div className="space-y-2">
                              <Label className="text-xs font-medium flex items-center gap-1">
                                <Zap className="h-3 w-3 text-amber-500" />
                                Volume Threshold
                              </Label>
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-500">$</span>
                                <Input
                                  type="number"
                                  value={doc.processingVolumeThreshold || ''}
                                  onChange={(e) =>
                                    updateDocumentRequirement(index, {
                                      processingVolumeThreshold: e.target.value
                                        ? parseInt(e.target.value)
                                        : undefined,
                                    })
                                  }
                                  placeholder="No threshold"
                                  className="h-8 text-sm"
                                />
                                <span className="text-xs text-gray-500">/month</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {doc.enabled && (
                        <div className="flex items-center gap-2">
                          {doc.required ? (
                            <Badge variant="secondary" className="text-xs bg-red-100 text-red-700 border-0">
                              Required
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs bg-gray-100 text-gray-600 border-0">
                              Optional
                            </Badge>
                          )}
                          <Switch
                            checked={doc.required}
                            onCheckedChange={(checked) =>
                              updateDocumentRequirement(index, { required: checked })
                            }
                            className="data-[state=checked]:bg-green-500 scale-75"
                          />
                        </div>
                      )}
                      
                      {!doc.documentType.startsWith('drivers_license') &&
                        !doc.documentType.startsWith('voided_check') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeDocumentRequirement(index)}
                            className="h-8 w-8 p-0 text-gray-400 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                    </div>
                  </div>

                  {doc.enabled && doc.processingVolumeThreshold && (
                    <div className="mt-3 ml-9">
                      <p className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-md inline-flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        Required when monthly volume exceeds ${doc.processingVolumeThreshold.toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              ))}

              <Button
                variant="outline"
                onClick={addDocumentRequirement}
                className="w-full border-dashed"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Document Requirement
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
});

MerchantApplicationSettings.displayName = 'MerchantApplicationSettings';
